import React from "react"
import autobind from "class-autobind"
import { Dialog, TextField, FlatButton, CircularProgress, FontIcon, IconButton } from "material-ui"
import { Table, TableBody, TableFooter, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from "material-ui/Table"
import { red500 } from "material-ui/styles/colors"
import strings from "../../../localization/strings"
import Clear from "../../../components/misc/Clear"
import AccountStore from "../../../stores/AccountStore"
import HierarchySettingsStore from "../../../stores/HierarchySettingsStore"
import { IClientDocument, IClientSearch, EClientActiveStatus } from "../../../models"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import * as api from "../../../api"

interface IProps {
	handleClose(): void
	handleSave(client: IClientDocument): void
}
interface IState {
	clients: IClientDocument[]
	selectedClient: IClientDocument
	selectedActiveStatus: EClientActiveStatus | "all"
	search: string
	loading: boolean
	total: number
	offset: number
}

export default class ChooseClientDialogForChip extends React.Component<IProps, IState> {
	private limit
	constructor(props) {
		super(props)
		this.state = {
			clients: [],
			selectedClient: null,
			selectedActiveStatus: EClientActiveStatus.Active,
			search: "",
			loading: true,
			offset: 0,
			total: 0
		}
		autobind(this)
		const tempLimit = Math.floor(window.innerHeight / 50) - 9
		this.limit = tempLimit > 8 ? 8 : tempLimit
	}

	componentDidMount() {
		this.getClients()
	}

	getClients() {
		api.getClients(AccountStore.getCurrentAccount().Id, HierarchySettingsStore.getGlobalLevelItemFilterId(), this.state.offset / this.limit, this.limit, this.state.search, this.state.selectedActiveStatus)
			.then(res => {
				const data = res.data as IClientSearch
				this.setState({
					loading: false,
					total: data.TotalDocumentCount,
					clients: data.Documents,
				})
			})
	}

	offsetChange(offset) {
		this.setState({
			offset,
			loading: true,
			selectedClient: null
		}, () =>
			this.getClients()
		)
	}

	render() {
		return (
			<Dialog
				modal
				open
				title={strings.Select_Client}
				actions={[
					<FlatButton
						data-intercom-target={"Client-Btn-CloseClientSelectionDialog"}
						label={strings.Cancel}
						secondary
						onClick={this.props.handleClose}
					/>,
					<FlatButton
						data-intercom-target={"Btn-ChooseSelectedClient"}
						label={strings.OK}
						primary
						keyboardFocused
						onClick={() => this.props.handleSave(this.state.selectedClient)}
					/>
				]}
				onRequestClose={this.props.handleClose}
			>
				<div style={{ padding: 20, paddingLeft: 35 }}>
					<i style={{ fontSize: 25 }} className="fa fa-search" />
					<TextField
						data-intercom-target={"Client-Entry-Search"}
						hintText={strings.search}
						style={{ marginRight: 20, width: "90%", marginLeft: 15 }}
						value={this.state.search}
						onChange={e =>
							this.setState({
								search: e.target.value,
								offset: 0,
								loading: true,
								selectedClient: null
							}, () =>
								this.getClients()
							)
						}
					/>
					<Clear />
				</div>
				<div style={{ textAlign: "center", height: 500 }}>
					{this.state.loading ?
						<CircularProgress /> :
						<Table
							onCellClick={rowNumber => this.setState({ selectedClient: this.state.clients[rowNumber] })}
							multiSelectable={false}
						>
							<TableHeader>
								<TableRow>
									<TableHeaderColumn style={{ textAlign: "left", width: 200 }}>{strings.Clients}</TableHeaderColumn>
									<TableHeaderColumn style={{ textAlign: "left" }}>{strings.HIERARCHY}</TableHeaderColumn>
								</TableRow>
							</TableHeader>
							<TableBody showRowHover deselectOnClickaway>
								{this.state.clients.map((client, index) => {
									const hierarchyPathLabelToRoot = hierarchyHelper.getFullPathLabel(client.HierarchyId)
									return (
										<TableRow data-intercom-target={`SelectClient${index}`} key={index} selected={this.state.selectedClient === client}>
											<TableRowColumn style={{ textAlign: "left", cursor: "pointer", width: 200 }} title={client.Name}>
												{client.Name}
											</TableRowColumn>
											<TableRowColumn style={{ textAlign: "left", cursor: "pointer", color: hierarchyPathLabelToRoot === strings.Hierarchy_Removed && red500 }} title={hierarchyPathLabelToRoot === strings.Hierarchy_Removed ? "" : hierarchyPathLabelToRoot}>
												{hierarchyPathLabelToRoot}
											</TableRowColumn>
										</TableRow>
									)
								})}
							</TableBody>
							<TableFooter>
								<TableRow className="TableFooter" style={{ borderBottom: 0 }}>
									<TableRowColumn style={{ float: "right" }}>
										<IconButton
											data-intercom-target={"ChooseClient-Btn-OpenFirstPage"}
											disabled={this.state.offset === 0}
											onClick={() => this.offsetChange(0)}
										>
											<FontIcon className="fa fa-step-backward" />
										</IconButton>
										<IconButton
											data-intercom-target={"ChooseClient-Btn-OpenPreviousPage"}
											disabled={this.state.offset === 0}
											onClick={() => this.offsetChange(this.state.offset - this.limit)}
										>
											<FontIcon className="fa fa-chevron-left" />
										</IconButton>
										<IconButton
											data-intercom-target={"ChooseClient-Btn-OpenNextPage"}
											disabled={this.state.offset + this.limit >= this.state.total}
											onClick={() => this.offsetChange(this.state.offset + this.limit)}
										>
											<FontIcon className="fa fa-chevron-right" />
										</IconButton>
										<IconButton
											data-intercom-target={"ChooseClient-Btn-OpenLastPage"}
											disabled={this.state.offset + this.limit >= this.state.total}
											onClick={() => this.state.total % this.limit === 0 ?
												this.offsetChange(this.state.total - this.limit) :
												this.offsetChange(this.state.total - (this.state.total % this.limit))
											}
										>
											<FontIcon className="fa fa-step-forward" />
										</IconButton>
									</TableRowColumn>
								</TableRow>
							</TableFooter>
						</Table>
					}
				</div>
			</Dialog>
		)
	}
}
